import { CallCenterLocation, CorporationConfiguration } from '../util/Corporations';

/** Env-configured base URLs are easy to typo with a trailing slash, which
 * would otherwise double up against the leading slash in each path below. */
const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

/**
 * `?staging=true` lets any deployed build (preview or prod) talk to staging
 * RapidStor/mtas without touching Cloudflare's build config — env vars there
 * apply to every branch alike, so this is the only safe way to point a
 * specific visit at staging. automate has no separate staging instance, so
 * it's untouched by this flag either way.
 */
export const IS_STAGING =
    new URLSearchParams(window.location.search).get('staging') === 'true';

export const RAPIDSTOR_BASE_URL = stripTrailingSlash(
    IS_STAGING
        ? 'https://api.rapidstorapp.com'
        : import.meta.env.VITE_RAPIDSTOR_BASE_URL ?? 'https://prod.rapidstorapp.com'
);
export const AUTOMATE_BASE_URL = stripTrailingSlash(
    import.meta.env.VITE_AUTOMATE_BASE_URL ?? 'https://app.r6automate.com'
);
export const MTAS_BASE_URL = stripTrailingSlash(
    IS_STAGING
        ? 'https://mtas.r6staging.com'
        : import.meta.env.VITE_MTAS_BASE_URL ??
              'https://multi-tenant-auth-service.r6clients.com'
);

// A locally-built (`npm run build && npm run preview`) or otherwise deployed
// bundle — must be a production-shaped build (single bundled <script> in
// <head>), not a raw `vite dev` server: dev mode's injected React-Refresh
// preamble is an inline module script, and inline module scripts resolve
// their imports against the current page's origin, not the origin they were
// fetched from — so it can never work cross-origin against a dev server.
export const JBL_WIDGET_BASE_URL =
    import.meta.env.VITE_JBL_WIDGET_BASE_URL ?? 'https://rapidstor-v2-frontend.pages.dev';

// Separate from which bundle we load: the widget itself decides prod vs its
// own dev API base purely from `journey.dev` in the config we hand it — the
// bundle running locally doesn't change that on its own.
export const JBL_WIDGET_DEV_MODE = import.meta.env.VITE_JBL_WIDGET_DEV_MODE === 'true';

export const USER_UUID_STORAGE_KEY = 'callcenter-user-uuid';
const FETCH_TIMEOUT_MS = 10000;

/**
 * fetch() with a hard timeout so a stalled backend can't hang the bootstrap
 * flow forever. On timeout, throws `timeoutMessage` so callers see the same
 * context-specific error they'd get from a real (non-hanging) failure.
 */
async function fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeoutMessage: string,
    timeoutMs: number = FETCH_TIMEOUT_MS
): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } catch (err) {
        throw new Error(timeoutMessage, { cause: err });
    } finally {
        clearTimeout(timeout);
    }
}

interface RapidStorLocation {
    sLocationCode: string;
    sSiteName: string;
    bSiteDisabled: boolean | string;
    /** The real site identifier the booking widgets need as their "site"
     * token — NOT sLocationCode, which is just a short display code. */
    token: string;
}

interface RapidStorLocationsResponse {
    operation_result: string;
    operation_message?: string;
    results: RapidStorLocation[];
}

interface MtasLocation {
    code: string;
    name: string;
    hidden: boolean;
    /** The real underlying RapidStor/SiteLink location code. For
     * MultiSiteLink corps, several of these `code`s can share the same
     * `rapidstor_code` — several physical locations, one SiteLink record. */
    rapidstor_code: string;
}

interface MtasLocationsResponse {
    data: MtasLocation[];
}

/**
 * Live per-location list for a corp. mtas's own `locations` table is the
 * source of truth for *which physical locations exist and what they're
 * called* (RapidStor's own /locations API is too coarse for MultiSiteLink
 * corps like CRUK, where several physical locations share one underlying
 * SiteLink record) — but the real `token` each location needs for actual
 * booking requests only exists on RapidStor's side. So: fetch both, and
 * resolve each mtas location's token via its `rapidstor_code`.
 *
 * Replaces a static, hand-typed `locations` array that went stale without
 * anyone noticing.
 */
export async function fetchLocations(corpCode: string): Promise<CallCenterLocation[]> {
    const [mtasLocations, sitelinkTokensByCode] = await Promise.all([
        fetchMtasLocations(corpCode),
        fetchSitelinkTokens(corpCode),
    ]);

    return mtasLocations
        .filter((location) => !location.hidden)
        .map((location) => {
            const token = sitelinkTokensByCode.get(location.rapidstor_code);
            if (!token) {
                console.warn(
                    `No RapidStor token found for ${corpCode}/${location.code} (rapidstor_code ${location.rapidstor_code}) — skipping`
                );
                return null;
            }
            return {
                code: location.code,
                name: location.name,
                locationCode: token,
                sitelinkCode: location.rapidstor_code,
            };
        })
        .filter((location): location is CallCenterLocation => location !== null);
}

async function fetchMtasLocations(corpCode: string): Promise<MtasLocation[]> {
    const failureMessage = `Failed to fetch locations for corp "${corpCode}"`;
    const response = await fetchWithTimeout(
        `${MTAS_BASE_URL}/api/v1/corporations/${encodeURIComponent(corpCode)}/locations`,
        { headers: { Accept: 'application/json' } },
        failureMessage
    );

    if (!response.ok) {
        throw new Error(failureMessage);
    }

    const body: MtasLocationsResponse = await response.json();
    return body.data;
}

async function fetchSitelinkTokens(corpCode: string): Promise<Map<string, string>> {
    const failureMessage = `Failed to fetch RapidStor locations for corp "${corpCode}"`;
    const response = await fetchWithTimeout(
        `${RAPIDSTOR_BASE_URL}/api/v1/locations`,
        { headers: { corp: corpCode } },
        failureMessage
    );

    if (!response.ok) {
        throw new Error(failureMessage);
    }

    const body: RapidStorLocationsResponse = await response.json();

    if (body.operation_result !== 'success') {
        throw new Error(
            `RapidStor rejected the locations request for corp "${corpCode}": ${body.operation_message}`
        );
    }

    return new Map(
        body.results
            .filter(
                (location) =>
                    location.bSiteDisabled !== true && location.bSiteDisabled !== 'true'
            )
            .map((location) => [location.sLocationCode, location.token])
    );
}

/**
 * Falls back to a fresh, non-persisted UUID if storage is unavailable
 * (privacy mode, disabled storage, etc.) — this UUID is only used for A/B
 * attribution on the widget's own config types, not the callcenter type this
 * app requests, so losing persistence here should never block bootstrap.
 */
function getOrCreateUserUuid(): string {
    try {
        const existing = localStorage.getItem(USER_UUID_STORAGE_KEY);
        if (existing) {
            return existing;
        }
        const uuid = crypto.randomUUID();
        localStorage.setItem(USER_UUID_STORAGE_KEY, uuid);
        return uuid;
    } catch {
        try {
            return crypto.randomUUID();
        } catch {
            return `transient-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
    }
}

interface CallCenterJourneyData {
    variant: CorporationConfiguration['bookingMode'];
    corpCode: string;
    corpToken: string;
    apiToken: string;
    themeColor: string;
    secondaryColor: string;
    baseUrl: string;
    pricingCalculator?: CorporationConfiguration['pricingCalculator'];
    pricingTable?: NonNullable<
        CorporationConfiguration['appConfig']['journey']['pricingTable']
    >;
    unitTypeRegex?: string;
    sourceInputType?: CorporationConfiguration['sourceInputType'];
    sourceOptions?: CorporationConfiguration['sourceOptions'];
    agentFieldType?: CorporationConfiguration['agentFieldType'];
}

interface TenantJourneyResponse {
    data: {
        id: string | null;
        data: {
            pages: CorporationConfiguration['appConfig']['pages'];
            journey: CallCenterJourneyData;
        };
    };
}

/**
 * Corp config for one specific site, sourced from automate's existing
 * `rapidstor_config` mechanism (type='callcenter'), the same one already
 * driving the JBL widget's own journey selection — reused instead of a new
 * config system.
 *
 * Returns `null` (not an error) when that specific site has no config row —
 * callers should surface that as "this location isn't set up" rather than
 * silently trying a different site, since some fields (e.g. a MultiSiteLink
 * `unitTypeRegex`) are genuinely site-specific and shouldn't be substituted.
 */
export async function fetchCorpConfig(
    corpCode: string,
    siteCode: string
): Promise<CorporationConfiguration | null> {
    const failureMessage = `Failed to fetch config for corp "${corpCode}"`;
    const response = await fetchWithTimeout(
        `${AUTOMATE_BASE_URL}/api/v1/tenant-journey/config`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                corp: corpCode,
                site: siteCode,
                type: 'callcenter',
                uuid: getOrCreateUserUuid(),
            }),
        },
        failureMessage
    );

    if (!response.ok) {
        throw new Error(failureMessage);
    }

    const body: TenantJourneyResponse = await response.json();

    // automate always returns HTTP 200 with a fallback object (id: null) when
    // no config row exists for this corp — that's our "not configured" signal.
    if (body.data.id === null) {
        return null;
    }

    const { journey, pages } = body.data.data;

    return {
        appConfig: {
            pages,
            journey: {
                locationCode: 'TEST',
                corpCode: journey.corpCode,
                apiToken: journey.apiToken,
                corpToken: journey.corpToken,
                themeColor: journey.themeColor,
                pricingTable: journey.pricingTable,
                unitTypeRegex: journey.unitTypeRegex,
            },
        },
        bookingMode: journey.variant,
        secondaryColor: journey.secondaryColor,
        baseUrl: journey.baseUrl,
        pricingCalculator: journey.pricingCalculator,
        sourceInputType: journey.sourceInputType,
        sourceOptions: journey.sourceOptions,
        agentFieldType: journey.agentFieldType,
    };
}
